/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { vehicleApi } from '../../../api/vehicleApi';
import { useSafeClose } from '../../../hooks/useSafeClose';
import './VehicleStatus.css';

const DOC_TYPES = [
    { value: 'TÜV', label: 'TÜV-Bericht' },
    { value: 'Inspektion', label: 'Inspektionsnachweis' },
    { value: 'UVV', label: 'UVV / Prüfung' },
    { value: 'Rechnung', label: 'Rechnung' },
    { value: 'Sonstiges', label: 'Sonstiges Dokument' }
];

export default function VehicleDocumentModal({
    isOpen,
    onClose,
    vehicle
}) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('TÜV');
    const [selectedFile, setSelectedFile] = useState(null);
    const [notes, setNotes] = useState('');
    const [sendToEmail, setSendToEmail] = useState('');
    const [shouldSendEmail, setShouldSendEmail] = useState(false);

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    useEffect(() => {
        if (isOpen && vehicle) {
            fetchDocuments();
        }
    }, [isOpen, vehicle]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const data = await vehicleApi.getVehicleDocuments(vehicle.id);
            setDocuments(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('document_type', activeTab);
            formData.append('notes', notes);
            if (shouldSendEmail && sendToEmail) {
                formData.append('send_to_email', sendToEmail);
            }

            await vehicleApi.uploadDocument(vehicle.id, formData);
            setSelectedFile(null);
            setNotes('');
            fetchDocuments();
        } catch (err) {
            alert('Upload fehlgeschlagen: ' + (err.response?.data?.error || err.message));
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm('Dokument wirklich löschen?')) return;
        try {
            await vehicleApi.deleteDocument(vehicle.id, docId);
            fetchDocuments();
        } catch (err) {
            console.error(err);
        }
    };

    if (!isOpen || !vehicle) return null;

    const filteredDocs = documents.filter(doc => doc.document_type === activeTab);

    return (
        <div className="modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="modal-content doc-database-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-title">📄 PDF-Datenbank: {vehicle.internal_name}</div>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body doc-modal-grid">
                    {/* Seitenleiste / Tabs */}
                    <div className="doc-tabs">
                        {DOC_TYPES.map(type => (
                            <button 
                                key={type.value}
                                className={`doc-tab-btn ${activeTab === type.value ? 'active' : ''}`}
                                onClick={() => setActiveTab(type.value)}
                            >
                                {type.label}
                                <span className="doc-count">
                                    {documents.filter(d => d.document_type === type.value).length}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Content Area */}
                    <div className="doc-content">
                        <div className="upload-section">
                            <h4>Neu hochladen ({activeTab})</h4>
                            <div className="upload-controls">
                                <input 
                                    type="file" 
                                    id="fileInput" 
                                    onChange={handleFileChange} 
                                    accept=".pdf,image/*"
                                    style={{ display: 'none' }}
                                />
                                <input 
                                    type="file" 
                                    id="cameraInput" 
                                    onChange={handleFileChange} 
                                    accept="image/*"
                                    capture="environment"
                                    style={{ display: 'none' }}
                                />
                                <div className="upload-buttons-row">
                                    <label htmlFor="fileInput" className="file-label-round" title="Datei hochladen">
                                        📁
                                    </label>
                                    <label htmlFor="cameraInput" className="camera-label-round" title="Foto aufnehmen">
                                        📷
                                    </label>
                                </div>
                                <div className="selected-file-name">
                                    {selectedFile ? selectedFile.name : 'Keine Datei ausgewählt'}
                                </div>
                                <input 
                                    type="text"
                                    className="doc-notes-input"
                                    placeholder="Optionale Notiz..."
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                />
                                <button 
                                    className={`btn-upload ${selectedFile ? 'ready' : ''}`}
                                    onClick={handleUpload}
                                    disabled={!selectedFile || uploading}
                                >
                                    {uploading ? 'Lädt...' : 'Hochladen'}
                                </button>
                            </div>

                            {/* Email Notification Option */}
                            <div className="email-notification-option">
                                <label className="email-toggle">
                                    <input 
                                        type="checkbox" 
                                        checked={shouldSendEmail} 
                                        onChange={e => setShouldSendEmail(e.target.checked)} 
                                    />
                                    <span>Datei nach Upload versenden an:</span>
                                </label>
                                {shouldSendEmail && (
                                    <input 
                                        type="email" 
                                        className="email-input-field"
                                        placeholder="E-Mail-Adresse..."
                                        value={sendToEmail}
                                        onChange={e => setSendToEmail(e.target.value)}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="doc-list-area">
                            <h4>Vorhandene Dokumente</h4>
                            {loading ? (
                                <div className="loading-state">Lädt Dokumente...</div>
                            ) : filteredDocs.length === 0 ? (
                                <div className="empty-state">Keine Dokumente in dieser Kategorie vorhanden.</div>
                            ) : (
                                <div className="doc-grid">
                                    {filteredDocs.map(doc => (
                                        <div key={doc.id} className="doc-card">
                                            <div className="doc-icon">PDF</div>
                                            <div className="doc-info">
                                                <div className="doc-name" title={doc.file_name}>{doc.file_name}</div>
                                                <div className="doc-meta">
                                                    {new Date(doc.upload_date).toLocaleDateString('de-DE')}
                                                    {doc.notes && <span className="doc-card-notes"> • {doc.notes}</span>}
                                                </div>
                                            </div>
                                            <div className="doc-actions">
                                                <a 
                                                    href={`http://localhost:3001/uploads/vehicles/${doc.file_path.split(/[\\/]/).pop()}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="btn-doc-view"
                                                >
                                                    👁️
                                                </a>
                                                <button 
                                                    className="btn-doc-delete"
                                                    onClick={() => handleDelete(doc.id)}
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-modal-cancel" onClick={onClose}>Schließen</button>
                </div>
            </div>
        </div>
    );
}
